import { useEffect, useState } from "react";

import CreateProjectHeader from "./CreateProjectHeader";
import CreateProjectNavigation from "./CreateProjectNavigation";

import ProjectBasicInfo from "../forms/ProjectBasicInfo";
import ProjectPlanning from "../forms/ProjectPlanning";
import ProjectReview from "../forms/ProjectReview";

import {
  createProject,
  getOrganizations,
} from "../services/project.service";

import { getEmployees } from "../services/employeeService";

const initialFormData = {
  project_code: "",
  project_name: "",
  project_type: "",
  execution_mode: "",
  priority: "Medium",

  organization_uuid: "",

  // Selected employee UUID will be stored here
  project_manager_employee_uuid: "",

  planned_start_date: "",
  planned_end_date: "",

  estimated_hours: "",
  estimated_cost: "",

  client_name: "",
  description: "",

  status: "Draft",
};

const CreateProjectDrawer = ({
  open,
  onClose,
  onProjectCreated,
}) => {
  const [activeStep, setActiveStep] = useState("basic");

  const [formData, setFormData] =
    useState(initialFormData);

  const [organizations, setOrganizations] =
    useState([]);

  const [employees, setEmployees] =
    useState([]);

  const [loadingData, setLoadingData] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] = useState("");

  // ==========================================
  // LOAD ORGANIZATIONS + EMPLOYEES
  // ==========================================

  useEffect(() => {
    if (!open) return;

    const loadData = async () => {
      try {
        setLoadingData(true);
        setError("");

        const [
          organizationResponse,
          employeeResponse,
        ] = await Promise.all([
          getOrganizations(),
          getEmployees(),
        ]);

        const organizationList =
          Array.isArray(organizationResponse)
            ? organizationResponse
            : organizationResponse?.data || [];

        const employeeList =
          Array.isArray(employeeResponse)
            ? employeeResponse
            : employeeResponse?.data || [];

        setOrganizations(organizationList);
        setEmployees(employeeList);

        console.log(
          "Organizations:",
          organizationList
        );

        console.log(
          "Employees:",
          employeeList
        );
      } catch (err) {
        console.error(
          "LOAD PROJECT DATA ERROR:",
          err
        );

        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to load organizations and employees."
        );
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [open]);

  // ==========================================
  // CLOSE
  // ==========================================

  const handleClose = () => {
    if (loading) return;

    setFormData({
      ...initialFormData,
    });

    setActiveStep("basic");
    setOrganizations([]);
    setEmployees([]);
    setError("");

    onClose();
  };

  // ==========================================
  // CREATE PROJECT
  // ==========================================

  const handleCreateProject = async () => {
    try {
      setLoading(true);
      setError("");

      if (!formData.organization_uuid) {
        setError(
          "Please select an organization."
        );
        return;
      }

      if (
        !formData.project_manager_employee_uuid
      ) {
        setError(
          "Please select a project manager."
        );
        return;
      }

      const payload = {
        ...formData,

        estimated_hours:
          formData.estimated_hours === ""
            ? null
            : Number(formData.estimated_hours),

        estimated_cost:
          formData.estimated_cost === ""
            ? null
            : Number(formData.estimated_cost),

        planned_start_date:
          formData.planned_start_date || null,

        planned_end_date:
          formData.planned_end_date || null,

        client_name:
          formData.client_name || null,

        description:
          formData.description || null,
      };

      console.log(
        "CREATE PROJECT PAYLOAD:",
        payload
      );

      const response =
        await createProject(payload);

      const createdProject =
        response?.data || response;

      const projectUuid =
        createdProject?.uuid;

      if (!projectUuid) {
        setError(
          "Project created but UUID was not returned."
        );
        return;
      }

      const projectWithUuid = {
        ...createdProject,

        uuid: projectUuid,
        project_uuid: projectUuid,

        organizationUuid:
          createdProject.organization_uuid ||
          formData.organization_uuid,

        projectCode:
          createdProject.project_code,

        projectName:
          createdProject.project_name,

        projectType:
          createdProject.project_type,

        executionMode:
          createdProject.execution_mode,

        projectManagerEmployeeUuid:
          createdProject.project_manager_employee_uuid ||
          formData.project_manager_employee_uuid,

        priority:
          createdProject.priority,

        plannedStartDate:
          createdProject.planned_start_date,

        plannedEndDate:
          createdProject.planned_end_date,

        estimatedHours:
          createdProject.estimated_hours,

        estimatedCost:
          createdProject.estimated_cost,

        clientName:
          createdProject.client_name,

        status:
          createdProject.status,

        description:
          createdProject.description,
      };

      console.log(
        "CREATED PROJECT:",
        projectWithUuid
      );

      onProjectCreated?.(
        projectWithUuid
      );

      handleClose();
    } catch (err) {
      console.error(
        "CREATE PROJECT ERROR:",
        err
      );

      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create project."
      );
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // NEXT
  // ==========================================

  const goNext = () => {
    setError("");

    if (activeStep === "basic") {
      if (!formData.organization_uuid) {
        setError(
          "Please select an organization."
        );
        return;
      }

      if (!formData.project_code.trim()) {
        setError(
          "Project code is required."
        );
        return;
      }

      if (!formData.project_name.trim()) {
        setError(
          "Project name is required."
        );
        return;
      }

      if (!formData.project_type) {
        setError(
          "Please select a project type."
        );
        return;
      }

      if (!formData.execution_mode) {
        setError(
          "Please select an execution mode."
        );
        return;
      }

      if (
        !formData.project_manager_employee_uuid
      ) {
        setError(
          "Please select a project manager."
        );
        return;
      }

      setActiveStep("planning");
      return;
    }

    if (activeStep === "planning") {
      setActiveStep("review");
    }
  };

  // ==========================================
  // BACK
  // ==========================================

  const goBack = () => {
    setError("");

    if (activeStep === "planning") {
      setActiveStep("basic");
      return;
    }

    if (activeStep === "review") {
      setActiveStep("planning");
    }
  };

  if (!open) return null;

  return (
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 z-[50] bg-black/30"
        onClick={handleClose}
      />

      {/* DRAWER */}
      <div className="fixed right-0 top-0 z-[60] flex h-screen w-full flex-col border-l border-slate-200 bg-slate-50 shadow-2xl md:w-[80%] lg:w-[70%] xl:w-1/2">

        {/* HEADER */}
        <CreateProjectHeader
          onClose={handleClose}
        />

        {/* NAVIGATION */}
        <CreateProjectNavigation
          activeStep={activeStep}
          setActiveStep={setActiveStep}
        />

        {/* CONTENT */}
        <div className="flex-1 overflow-y-auto p-6">

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
              {error}
            </div>
          )}

          {loadingData ? (
            <div className="flex min-h-[300px] items-center justify-center">
              <div className="text-center">
                <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-blue-600" />

                <p className="mt-3 text-sm text-slate-500">
                  Loading organizations and employees...
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* BASIC INFO */}
              {activeStep === "basic" && (
                <ProjectBasicInfo
                  formData={formData}
                  setFormData={setFormData}
                  organizations={organizations}
                  employees={employees}
                />
              )}

              {/* PLANNING */}
              {activeStep === "planning" && (
                <ProjectPlanning
                  formData={formData}
                  setFormData={setFormData}
                />
              )}

              {/* REVIEW */}
              {activeStep === "review" && (
                <ProjectReview
                  formData={formData}
                  employees={employees}
                />
              )}
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="flex items-center justify-between border-t border-slate-200 bg-white px-6 py-4">

          <button
            type="button"
            disabled={loading || loadingData}
            onClick={
              activeStep === "basic"
                ? handleClose
                : goBack
            }
            className="rounded-lg border border-slate-300 px-5 py-2.5 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            {activeStep === "basic"
              ? "Cancel"
              : "Back"}
          </button>

          <div>
            {activeStep !== "review" && (
              <button
                type="button"
                disabled={loadingData}
                onClick={goNext}
                className="rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
              >
                Next
              </button>
            )}

            {activeStep === "review" && (
              <button
                type="button"
                disabled={loading}
                onClick={handleCreateProject}
                className="rounded-lg bg-blue-600 px-6 py-2.5 font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-400"
              >
                {loading
                  ? "Creating..."
                  : "Create Project"}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default CreateProjectDrawer;